import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import classnames from 'classnames';

import { useCurrentUserAccountQuery } from '@interfaces/graphql-typings';

import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import styles from './userAccountPageStyles.scss';

const pageTitles: { [key: string]: string } = {
  ['/ideas']: 'Your Ideas',
  ['/ratings']: 'Your Ratings',
  ['/badges']: 'Your Badges',
  ['/search_alerts']: 'Your Saved Searches',
  ['/watchlist']: 'Your Watchlisted Tickers',
  ['/reading_list']: 'Your Reading List',
  ['/archived_reading_list']: 'Your Archived Reading List',
  ['/following']: 'People You Follow',
  ['/followers']: 'Your Followers',
  ['/job_postings']: 'Jobs Posted',
  ['/job_applications']: 'Jobs Applied To',
  ['/referrals']: 'Your Referrals',
  ['/']: 'Email Address & Password',
  ['/notifications']: 'Email Notifications',
  ['/site_notifications']: 'Site Notifications',
  ['/billing_information']: 'Billing Information',
  ['/viewer_intro_requests']: 'Received Intro Requests',
  ['/compensations']: 'Compensation'
};

import UserAccountShowPage from '@buyside/components/presentation/UserAccount/ChangeEmailPassword/userAccountShowPage';
import UserBadgesPage from '@buyside/components/presentation/UserAccount/YourBadges/userBadgesPage';
import UserCompensationsPage from '@buyside/components/presentation/UserAccount/PostedCompensations/userCompensationsPage';
import UserFollowersPage from '@buyside/components/presentation/UserAccount/UsersFollowingYou/userFollowersPage';
import UserFollowingPage from '@buyside/components/presentation/UserAccount/UsersYouFollow/userFollowingPage';
import UserIdeasPage from '@buyside/components/presentation/UserAccount/Ideas/userIdeasPage';
import UserJobApplicationsPage from '@buyside/components/presentation/UserAccount/JobsAppliedTo/userJobApplicationsPage';
import UserJobPostingsPage from '@buyside/components/presentation/UserAccount/JobsPosted/userJobPostingsPage';
import UserNotificationsShowPage from '@buyside/components/presentation/UserAccount/EmailNotifications/userNotificationsShowPage';
import UserRatingsPage from '@buyside/components/presentation/UserAccount/YourRatings/userRatingsPage';
import UserReadingListPage from '@buyside/components/presentation/UserAccount/ReadingList/userReadingListPage';
import UserReferralsShowPage from '@buyside/components/presentation/UserAccount/YourReferrals/userReferralsShowPage';
import RetailUserReferralsShowPage from '@buyside/components/presentation/UserAccount/YourReferrals/retailUserReferralsShowPage';
import UserSavedSearchesPage from '@buyside/components/presentation/UserAccount/SavedSearches/userSavedSearchesPage';
import UserSiteNotificationsPage from '@buyside/components/presentation/UserAccount/SiteNotifications/userSiteNotificationsPage';
import UserWatchlistPage from '@buyside/components/presentation/UserAccount/WatchlistedTickers/userWatchlistPage';
import BillingInformationShowPage from '@buyside/components/presentation/UserAccount/BillingInformation/billingInformationShowPage';
import UserViewerIntroRequestsPage from '@buyside/components/presentation/UserAccount/ViewerIntroRequests/userViewerIntroRequestsPage';
import UserAccountLeftSideBar from '@buyside/components/presentation/UserAccount/userAccountLeftSideBar';
import TriLineIcon from '@buyside/svgs/tri_line.svg';

const UserAccountPageContainer: React.FC = (): React.ReactElement => {
  const { loading, data } = useCurrentUserAccountQuery();

  const [showMobileNav, toggleShowMobileNav] = useState(false);

  const { pathname } = useLocation();
  const cleanPath = '/' + pathname.split('/')[1];

  useEffect(() => toggleShowMobileNav(false), [cleanPath]);
  useEffect(() => window.scrollTo({ top: 0 }), [pathname]);

  if (loading || !data?.currentUser) return <LoadingSpinner size={SIZES.LARGE} />;

  const { isTokenBased } = data.currentUser;

  return (
    <div className={styles.container}>
      <div className={styles.leftSidebar}>
        <div className={styles.mobileNavTitle}>
          <TriLineIcon
            className={styles.mobileNavIcon}
            onClick={(): void => toggleShowMobileNav(!showMobileNav)}
          />
          <span className={styles.title}>{pageTitles[cleanPath] || 'Account'}</span>
        </div>
        <div
          className={classnames(styles.sidebarOptions, {
            [styles.showMobileOptions]: showMobileNav
          })}
        >
          <UserAccountLeftSideBar pathname={cleanPath} />
        </div>
      </div>
      <div>
        <Routes>
          <Route path="/" element={<UserAccountShowPage />} />
          <Route path="/notifications" element={<UserNotificationsShowPage />} />
          <Route path="/site_notifications" element={<UserSiteNotificationsPage />} />
          <Route
            path="/referrals"
            element={isTokenBased ? <RetailUserReferralsShowPage /> : <UserReferralsShowPage />}
          />
          <Route path="/job_applications" element={<UserJobApplicationsPage />} />
          <Route path="/job_postings/:jobPostingId?" element={<UserJobPostingsPage />} />
          <Route path="/compensations" element={<UserCompensationsPage />} />
          <Route path="/followers" element={<UserFollowersPage />} />
          <Route path="/following" element={<UserFollowingPage />} />
          <Route path="/archived_reading_list" element={<UserReadingListPage archivedPage />} />
          <Route path="/reading_list" element={<UserReadingListPage />} />
          <Route path="/watchlist" element={<UserWatchlistPage />} />
          <Route path="/search_alerts" element={<UserSavedSearchesPage />} />
          <Route path="/badges" element={<UserBadgesPage />} />
          <Route path="/ratings" element={<UserRatingsPage />} />
          <Route path="/ideas" element={<UserIdeasPage />} />
          <Route path="/billing_information" element={<BillingInformationShowPage />} />
          <Route path="/viewer_intro_requests" element={<UserViewerIntroRequestsPage />} />
        </Routes>
      </div>
    </div>
  );
};

export default UserAccountPageContainer;
